import {
  AlarmClock,
  CircleAlert,
  Clock,
  ShieldCheck,
  ShieldOff,
  ShieldX,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * The one status vocabulary (CAP-7). Colour is never used alone: every badge
 * shows its fixed icon and its label, so it stays readable in greyscale.
 * The label is passed in already translated (`status.<kind>` keys).
 */

export const STATUS_KINDS = [
  "uninsured",
  "pending",
  "active",
  "expiring",
  "expired",
  "failed",
] as const;

export type StatusKind = (typeof STATUS_KINDS)[number];

type Tone = "success" | "warning" | "error" | "info";

export const STATUS_PRESENTATION: Record<StatusKind, { tone: Tone; icon: LucideIcon }> = {
  uninsured: { tone: "error", icon: ShieldOff },
  pending: { tone: "warning", icon: Clock },
  active: { tone: "success", icon: ShieldCheck },
  expiring: { tone: "warning", icon: AlarmClock },
  expired: { tone: "error", icon: ShieldX },
  failed: { tone: "error", icon: CircleAlert },
};

const TONE_CLASSES: Record<Tone, string> = {
  success: "bg-status-success text-status-success-foreground",
  warning: "bg-status-warning text-status-warning-foreground",
  error: "bg-status-error text-status-error-foreground",
  info: "bg-status-info text-status-info-foreground",
};

export interface StatusBadgeProps extends React.ComponentProps<"span"> {
  status: StatusKind;
  label: string;
}

function StatusBadge({ status, label, className, ...props }: StatusBadgeProps) {
  const { tone, icon: Icon } = STATUS_PRESENTATION[status];
  return (
    <span
      data-slot="status-badge"
      data-status={status}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-semibold",
        TONE_CLASSES[tone],
        className,
      )}
      {...props}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span>{label}</span>
    </span>
  );
}

export { StatusBadge };
